import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { Refine } from "@refinedev/core";
import React from "react";
import { AppLayout } from "./components/layout/AppLayout";
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
import { notificationProvider } from "./providers/notificationProvider";
import routerBindings, {
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";

// Providers
import { dataProvider } from "./providers/dataProvider";
import { authProvider } from "./providers/authProvider";
import { tenantResources } from "./config/resources";
import { useProductFeatures } from "@/hooks/useProductFeatures";
import axios from "axios";

/** Redirect LabBOXX legacy in-app User Management to admin.onprosoft.com
 *  when SaaSBOXX flag is off (default). Uses SSO handoff. */
const CentralUMGuard: React.FC<{ children: React.ReactNode; portalPath: string }> = ({ children, portalPath }) => {
  const { exposeLocalUserManagement } = useProductFeatures();
  React.useEffect(() => {
    if (exposeLocalUserManagement) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post('/api/v1/auth/sso/issue-portal-token/', { next: portalPath }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token') || ''}` },
        });
        if (cancelled) return;
        const url = r.data?.redirect_url;
        if (url) { window.location.replace(url); return; }
      } catch { /* fall through */ }
      if (!cancelled) {
        window.location.replace(`https://admin.onprosoft.com${portalPath}`);
      }
    })();
    return () => { cancelled = true; };
  }, [exposeLocalUserManagement, portalPath]);
  if (!exposeLocalUserManagement) {
    return <div className="min-h-[50vh] flex items-center justify-center text-sm text-muted-foreground">Signing you into Prosoft Workspace Admin…</div>;
  }
  return <>{children}</>;
};
import { getTenantDisplayName } from "./utils/tenantUtils";
import { PasswordChangeWrapper } from "./components/auth/PasswordChangeWrapper";

// Auth Pages
const TenantAuthPage = lazy(() => import("./pages/auth/TenantAuthPage"));
const SsoLanding = lazy(() => import("./pages/auth/SsoLanding"));
const Impersonate = lazy(() => import("./pages/auth/Impersonate"));
import EmailVerification from "./components/auth/EmailVerification";

// Dashboard
const DashboardPage = lazy(() => import("./pages/Dashboard").then(mod => ({ default: mod.DashboardPage })));
const FootfallDashboard = lazy(() => import("./pages/footfall/FootfallDashboard"));

// Patient Management (new module)
const PatientDirectoryPage = lazy(() => import("./pages/Patients/PatientDirectoryPage"));
const PatientCreatePage = lazy(() => import("./pages/Patients/PatientCreatePage"));
const PatientEditPage = lazy(() => import("./pages/Patients/PatientEditPage"));
const PatientShowPage = lazy(() => import("./pages/Patients/PatientShowPage"));
const ReferringDoctorsPage = lazy(() => import("./pages/Patients/ReferringDoctorsPage"));
const ReferringDoctorCreatePage = lazy(() => import("./pages/Patients/ReferringDoctorCreatePage"));
const ReferringDoctorEditPage = lazy(() => import("./pages/Patients/ReferringDoctorEditPage"));
const ReferringDoctorShowPage = lazy(() => import("./pages/Patients/ReferringDoctorShowPage"));

// Orders
const OrdersList = lazy(() => import("./pages/Orders").then(mod => ({ default: mod.OrdersList })));
const CreateOrderPage = lazy(() => import("./pages/Orders/CreateOrderPage"));
const EditOrderPage = lazy(() => import("./pages/Orders/EditOrderPage"));
const ShowOrderPage = lazy(() => import("./pages/Orders/ShowOrderPage"));
const AccessioningPage = lazy(() => import("./pages/Orders/AccessioningPage"));
const ReprintLabelsPage = lazy(() => import("./pages/Orders/ReprintLabelsPage"));
const WorklistPage = lazy(() => import("./pages/Orders/WorklistPage"));

// Laboratory
const QualityControlPage = lazy(() => import("./pages/Laboratory/QualityControl").then(mod => ({ default: mod.QualityControlPage })));
const AnalyzerResults = lazy(() => import("./pages/Laboratory/HL7MasterDetail/AnalyzerResults").then(mod => ({ default: mod.AnalyzerResults })));
const AppointmentsPage = lazy(() => import("./pages/Laboratory").then(mod => ({ default: mod.AppointmentsPage })));
const HomeVisitsPage = lazy(() => import("./pages/Laboratory").then(mod => ({ default: mod.HomeVisitsPage })));
const TestCatalogPage = lazy(() => import("./pages/Laboratory").then(mod => ({ default: mod.TestCatalogPage })));
const LabReportsPage = lazy(() => import("./pages/Laboratory/Reports/LabReportsPage"));
const ReferenceRangesPage = lazy(() => import("./pages/Laboratory/ReferenceRanges/ReferenceRangesPage"));

// Results
const ResultsWorklistPage = lazy(() => import("./pages/results/ResultsWorklistPage"));
const PathologistReviewPage = lazy(() => import("./pages/results/PathologistReviewPage"));

// Samples
const SamplesList = lazy(() => import("./pages/Samples").then(mod => ({ default: mod.SamplesList })));

// Inventory
const InventoryList = lazy(() => import("./pages/Inventory").then(mod => ({ default: mod.InventoryList })));
const CategoriesPage = lazy(() => import("./pages/Inventory/CategoriesPage").then(mod => ({ default: mod.CategoriesPage })));
const StockMovementsPage = lazy(() => import("./pages/Inventory/StockMovementsPage").then(mod => ({ default: mod.StockMovementsPage })));
const CreateItem = lazy(() => import("./pages/Inventory/CreateItem").then(mod => ({ default: mod.CreateItem })));
const CreateStockMovement = lazy(() => import("./pages/Inventory/CreateStockMovement").then(mod => ({ default: mod.CreateStockMovement })));
const EditItem = lazy(() => import("./pages/Inventory/EditItem").then(mod => ({ default: mod.EditItem })));
const ShowItem = lazy(() => import("./pages/Inventory/ShowItem").then(mod => ({ default: mod.ShowItem })));
const SuppliersPage = lazy(() => import("./pages/Inventory/SuppliersPage").then(mod => ({ default: mod.SuppliersPage })));
const AddSupplierPage = lazy(() => import("./pages/Inventory/AddSupplierPage"));
const EditSupplierPage = lazy(() => import("./pages/Inventory/EditSupplierPage"));
const PurchaseOrdersPage = lazy(() => import("./pages/Inventory/PurchaseOrdersPage").then(mod => ({ default: mod.PurchaseOrdersPage })));
const PurchaseOrderForm = lazy(() => import("./pages/Inventory/PurchaseOrderForm").then(mod => ({ default: mod.PurchaseOrderForm })));
const PurchaseOrderShow = lazy(() => import("./pages/Inventory/PurchaseOrderShow").then(mod => ({ default: mod.PurchaseOrderShow })));
const GoodsReceiptsPage = lazy(() => import("./pages/Inventory/GoodsReceiptsPage").then(mod => ({ default: mod.GoodsReceiptsPage })));
const GoodsReceiptForm = lazy(() => import("./pages/Inventory/GoodsReceiptForm").then(mod => ({ default: mod.GoodsReceiptForm })));
const GoodsReceiptShow = lazy(() => import("./pages/Inventory/GoodsReceiptShow").then(mod => ({ default: mod.GoodsReceiptShow })));
const PurchaseInvoicesPage = lazy(() => import("./pages/Inventory/PurchaseInvoicesPage").then(mod => ({ default: mod.PurchaseInvoicesPage })));
const PurchaseInvoiceForm = lazy(() => import("./pages/Inventory/PurchaseInvoiceForm").then(mod => ({ default: mod.PurchaseInvoiceForm })));
const PurchaseInvoiceShow = lazy(() => import("./pages/Inventory/PurchaseInvoiceShow").then(mod => ({ default: mod.PurchaseInvoiceShow })));
const SupplierPaymentsPage = lazy(() => import("./pages/Inventory/SupplierPaymentsPage").then(mod => ({ default: mod.SupplierPaymentsPage })));
const SupplierPaymentForm = lazy(() => import("./pages/Inventory/SupplierPaymentForm").then(mod => ({ default: mod.SupplierPaymentForm })));
const SupplierPaymentShow = lazy(() => import("./pages/Inventory/SupplierPaymentShow").then(mod => ({ default: mod.SupplierPaymentShow })));
const DebitNotesPage = lazy(() => import("./pages/Inventory/DebitNotesPage").then(mod => ({ default: mod.DebitNotesPage })));
const PurchaseDocumentsPage = lazy(() => import("./pages/Inventory/PurchaseDocumentsPage").then(mod => ({ default: mod.PurchaseDocumentsPage })));
const PurchaseDocumentForm = lazy(() => import("./pages/Inventory/PurchaseDocumentForm").then(mod => ({ default: mod.PurchaseDocumentForm })));
const DebitNoteCreate = lazy(() => import("./pages/Inventory/DebitNoteCreate").then(mod => ({ default: mod.DebitNoteCreate })));
const DebitNoteShow = lazy(() => import("./pages/Inventory/DebitNoteShow").then(mod => ({ default: mod.DebitNoteShow })));

// Devices
import {
  ConnectorDevicesList,
  ConnectorDeviceCreate,
  ConnectorDeviceEdit,
  ConnectorDeviceShow,
  AnalyzerDevicesList,
  AnalyzerDeviceCreate,
  AnalyzerDeviceEdit,
  AnalyzerDeviceShow,
  ImagingDevicesList,
  ImagingDeviceCreate,
  ImagingDeviceEdit,
  ImagingDeviceShow,
  DeviceMonitoringDashboard,
} from "./pages/tenant/devices";

// Users
const TenantUsersPage = lazy(() => import("./pages/Users"));
const TenantRolesPage = lazy(() => import("./pages/Users/Roles"));
const AccessRoles = lazy(() => import("./pages/Users/AccessRoles"));
const TenantUserSessionsPage = lazy(() => import("./pages/Users/Sessions"));

// Core
import {
  CompanyProfile,
  BranchManagement,
  AddressManagement,
  NotificationSettings as CoreNotificationSettings,
  CompanyCreate,
  CompanyEdit,
  CompanyShow,
  BranchCreate,
  BranchEdit,
  BranchShow,
  AddressCreate,
  AddressEdit,
  AddressShow,
} from "./pages/Core";

// Settings
import TenantOrganizationSettings from "./components/settings/TenantOrganizationSettings";
const CurrencyManagementPage = lazy(() => import("./pages/Core/CurrencyManagementPage"));
const DangerZone = lazy(() => import("./pages/settings/DangerZone"));
import UnifiedNotificationTemplateManager from "./components/settings/UnifiedNotificationTemplateManager";

// Core Settings (2026-09-10)
const BrandManagement = lazy(() => import("./pages/Core/BrandManagement"));
const AppearanceSettings = lazy(() => import("./pages/Core/AppearanceSettings"));
const DocumentNumbering = lazy(() => import("./pages/Core/DocumentNumbering"));
const HolidayCalendar = lazy(() => import("./pages/Core/HolidayCalendar"));
const ContainerTypes = lazy(() => import("./pages/Core/ContainerTypes"));
const SettingsAuditTrail = lazy(() => import("./pages/Core/SettingsAuditTrail"));
import { BrandTheme } from "./components/BrandTheme";

// Billing
const BillingEntitiesPage = lazy(() => import("./pages/billing/BillingEntitiesPage"));
const InvoicesPage = lazy(() => import("./pages/billing/InvoicesPage"));
const DocumentStudio = lazy(() => import("./pages/DocumentStudio"));
const PaymentsPage = lazy(() => import("./pages/billing/PaymentsPage"));
const BillingEntityCreate = lazy(() => import("./pages/billing/BillingEntityCreate"));
const BillingEntityEdit = lazy(() => import("./pages/billing/BillingEntityEdit"));
const BillingEntityShow = lazy(() => import("./pages/billing/BillingEntityShow"));
const InvoiceCreate = lazy(() => import("./pages/billing/InvoiceCreate"));
const InvoiceEdit = lazy(() => import("./pages/billing/InvoiceEdit"));
const InvoiceShow = lazy(() => import("./pages/billing/InvoiceShow"));
const PaymentCreate = lazy(() => import("./pages/billing/PaymentCreate"));
const PaymentEdit = lazy(() => import("./pages/billing/PaymentEdit"));
const PaymentShow = lazy(() => import("./pages/billing/PaymentShow"));

// Tax Management
const TaxTypesPage = lazy(() => import("./pages/billing/TaxTypesPage"));
const TaxTypeCreate = lazy(() => import("./pages/billing/TaxTypeCreate"));
const TaxTypeEdit = lazy(() => import("./pages/billing/TaxTypeEdit"));
const TaxTypeShow = lazy(() => import("./pages/billing/TaxTypeShow"));
const TaxExemptionsPage = lazy(() => import("./pages/billing/TaxExemptionsPage"));
const TaxExemptionCreate = lazy(() => import("./pages/billing/TaxExemptionCreate"));
const TaxExemptionEdit = lazy(() => import("./pages/billing/TaxExemptionEdit"));
const TaxConfigurationsPage = lazy(() => import("./pages/billing/TaxConfigurationsPage"));
const TaxConfigurationCreate = lazy(() => import("./pages/billing/TaxConfigurationCreate"));
const TaxCalculationsPage = lazy(() => import("./pages/billing/TaxCalculationsPage"));
const TaxCalculationCreate = lazy(() => import("./pages/billing/TaxCalculationCreate"));

// Support
const SupportTickets = lazy(() => import("./pages/Support/SupportTickets"));
const KnowledgeBase = lazy(() => import("./pages/Support/KnowledgeBase"));

// Error Boundary
import { ErrorBoundary } from "./components/ErrorBoundary";
import { NoAppWithoutTenant } from "./lib/host";

// Authenticated layout wrapper with token monitoring
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tenantName = getTenantDisplayName();
  const location = useLocation();

  // Upfront auth gate. Without this, an anonymous user landing on
  // /laboratory/test-catalog (or any deep link) would render the
  // page, fire dozens of API calls, get 401s on every one, and pile
  // up error toasts. Bounce to /login first.
  const accessToken =
    typeof window !== "undefined" ? localStorage.getItem("jwt_access_token") : null;
  if (!accessToken) {
    const redirect = location.pathname + location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  // Token expiration monitor
  React.useEffect(() => {
    const checkTokenExpiration = () => {
      const tokenExpiry = localStorage.getItem("jwt_token_expiry");
      const accessToken = localStorage.getItem("jwt_access_token");
      if (!accessToken) return;

      if (tokenExpiry) {
        const timeUntilExpiry = new Date(tokenExpiry).getTime() - Date.now();
        const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60000);

        if (minutesUntilExpiry < 5 && minutesUntilExpiry > 0) {
          import("./services/jwtAuthService").then(({ jwtAuthService }) => {
            jwtAuthService.attemptRefresh().catch(() => {});
          });
        }

        if (timeUntilExpiry < 0) {
          localStorage.clear();
          window.location.href = "/login";
        }
      }
    };

    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AppLayout
      title={
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-bold text-primary">{tenantName}</span>
          <span className="text-xs text-muted-foreground">LabBOXX LIMS</span>
        </div>
      }
    >
      <PasswordChangeWrapper>{children}</PasswordChangeWrapper>
    </AppLayout>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        {/* labboxx.com itself draws only LabBOXX's landing page: sign-in
            exists only on a tenant's own address (owner, 2026-10-04/05).
            See lib/host. */}
        <NoAppWithoutTenant>
        <Refine
          dataProvider={dataProvider()}
          authProvider={authProvider}
          routerProvider={routerBindings}
          notificationProvider={notificationProvider}
          resources={tenantResources}
          options={{
            syncWithLocation: true,
            warnWhenUnsavedChanges: true,
            projectId: "labboxx-lims",
          }}
        >
          <Suspense fallback={<div className="flex items-center justify-center p-12 text-sm text-muted-foreground">Loading…</div>}>
          <Routes>
            {/* Auth routes */}
            <Route path="/login" element={<TenantAuthPage />} />
            <Route path="/sso/landing" element={<SsoLanding />} />
            {/* Cross-product 9-dot AppSwitcher landing — same component;
                path matches what SaaSBOXX entitlement feed advertises. */}
            <Route path="/sso/accept" element={<SsoLanding />} />
            <Route path="/auth/sso-handoff" element={<SsoLanding />} />
            {/* SaaSBOXX Superadmin button — seeds tokens from URL params */}
            <Route path="/impersonate" element={<Impersonate />} />
            <Route path="/verify-email" element={<EmailVerification />} />

            {/* Authenticated routes */}
            <Route
              element={
                <AuthenticatedLayout>
                  <Outlet />
                </AuthenticatedLayout>
              }
            >
              {/* Dashboard */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/document-studio" element={<DocumentStudio />} />
              <Route index element={<NavigateToResource resource="dashboard" />} />

              {/* Footfall analytics (SaaSBOXX footfall plane) */}
              <Route path="/footfall" element={<FootfallDashboard />} />

              {/* LabBOXX-InTutor — full-platform manual, tutorials & glossary */}
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/:topicId" element={<HelpCenter />} />

              {/* Patient Management */}
              <Route path="/patients" element={<PatientDirectoryPage />} />
              <Route path="/patients/create" element={<PatientCreatePage />} />
              <Route path="/patients/edit/:id" element={<PatientEditPage />} />
              <Route path="/patients/show/:id" element={<PatientShowPage />} />
              <Route path="/patients/doctors" element={<ReferringDoctorsPage />} />
              <Route path="/patients/doctors/create" element={<ReferringDoctorCreatePage />} />
              <Route path="/patients/doctors/edit/:id" element={<ReferringDoctorEditPage />} />
              <Route path="/patients/doctors/show/:id" element={<ReferringDoctorShowPage />} />

              {/* Orders */}
              <Route path="/orders" element={<OrdersList />} />
              <Route path="/orders/create" element={<CreateOrderPage />} />
              <Route path="/orders/edit/:id" element={<EditOrderPage />} />
              <Route path="/orders/show/:id" element={<ShowOrderPage />} />
              <Route path="/orders/accessioning" element={<AccessioningPage />} />
              <Route path="/orders/worklist" element={<WorklistPage />} />
              <Route path="/orders/reprint" element={<ReprintLabelsPage />} />

              {/* Laboratory */}
              <Route path="/laboratory/appointments" element={<AppointmentsPage />} />
              <Route path="/laboratory/home-visits" element={<HomeVisitsPage />} />
              <Route path="/laboratory/test-catalog" element={<TestCatalogPage />} />
              <Route path="/laboratory/reference-ranges" element={<ReferenceRangesPage />} />
              {/* Renamed: the screen carries raw serial output as well as HL7,
                  so it is no longer named after one protocol. The old path is
                  kept as a redirect — it is in bookmarks, in the help route
                  map and in public/quick-fix.html. */}
              <Route path="/laboratory/analyzer-results" element={<AnalyzerResults />} />
              <Route path="/laboratory/hl7-master-detail" element={<Navigate to="/laboratory/analyzer-results" replace />} />
              <Route path="/laboratory/quality-control" element={<QualityControlPage />} />
              <Route path="/laboratory/reports" element={<LabReportsPage />} />

              {/* Results */}
              <Route path="/results/worklist" element={<ResultsWorklistPage />} />
              <Route path="/results/pathologist-review" element={<PathologistReviewPage />} />

              {/* Inventory */}
              <Route path="/inventory/items" element={<InventoryList />} />
              <Route path="/inventory/items/create" element={<CreateItem />} />
              <Route path="/inventory/items/edit/:id" element={<EditItem />} />
              <Route path="/inventory/items/show/:id" element={<ShowItem />} />
              <Route path="/inventory/categories" element={<CategoriesPage />} />
              <Route path="/inventory/suppliers" element={<SuppliersPage />} />
              <Route path="/inventory/suppliers/create" element={<AddSupplierPage />} />
              <Route path="/inventory/suppliers/edit/:id" element={<EditSupplierPage />} />
              <Route path="/inventory/movements" element={<StockMovementsPage />} />
              <Route path="/inventory/movements/create" element={<CreateStockMovement />} />
              <Route path="/inventory/purchase-orders" element={<PurchaseOrdersPage />} />
              <Route path="/inventory/purchase-orders/create" element={<PurchaseOrderForm />} />
              <Route path="/inventory/purchase-orders/edit/:id" element={<PurchaseOrderForm />} />
              <Route path="/inventory/purchase-orders/show/:id" element={<PurchaseOrderShow />} />
              <Route path="/inventory/goods-receipts" element={<GoodsReceiptsPage />} />
              <Route path="/inventory/goods-receipts/create" element={<GoodsReceiptForm />} />
              <Route path="/inventory/goods-receipts/edit/:id" element={<GoodsReceiptForm />} />
              <Route path="/inventory/goods-receipts/show/:id" element={<GoodsReceiptShow />} />
              <Route path="/inventory/purchase-invoices" element={<PurchaseInvoicesPage />} />
              <Route path="/inventory/purchase-invoices/create" element={<PurchaseInvoiceForm />} />
              <Route path="/inventory/purchase-invoices/edit/:id" element={<PurchaseInvoiceForm />} />
              <Route path="/inventory/purchase-invoices/show/:id" element={<PurchaseInvoiceShow />} />
              <Route path="/inventory/supplier-payments" element={<SupplierPaymentsPage />} />
              <Route path="/inventory/supplier-payments/create" element={<SupplierPaymentForm />} />
              <Route path="/inventory/supplier-payments/apply/:id" element={<SupplierPaymentForm />} />
              <Route path="/inventory/supplier-payments/show/:id" element={<SupplierPaymentShow />} />
              <Route path="/inventory/supplier-payments/edit/:id" element={<SupplierPaymentForm />} />
              <Route path="/inventory/debit-notes" element={<DebitNotesPage />} />
              <Route path="/inventory/debit-notes/create" element={<DebitNoteCreate />} />
              <Route path="/inventory/debit-notes/show/:id" element={<DebitNoteShow />} />
              <Route path="/inventory/purchase-documents" element={<PurchaseDocumentsPage />} />
              <Route path="/inventory/purchase-documents/create" element={<PurchaseDocumentForm />} />
              <Route path="/inventory/purchase-documents/edit/:id" element={<PurchaseDocumentForm />} />

              {/* Devices */}
              <Route path="/devices/connectors" element={<ConnectorDevicesList />} />
              <Route path="/devices/connectors/create" element={<ConnectorDeviceCreate />} />
              <Route path="/devices/connectors/edit/:id" element={<ConnectorDeviceEdit />} />
              <Route path="/devices/connectors/show/:id" element={<ConnectorDeviceShow />} />
              <Route path="/devices/analyzers" element={<AnalyzerDevicesList />} />
              <Route path="/devices/analyzers/create" element={<AnalyzerDeviceCreate />} />
              <Route path="/devices/analyzers/edit/:id" element={<AnalyzerDeviceEdit />} />
              <Route path="/devices/analyzers/show/:id" element={<AnalyzerDeviceShow />} />
              <Route path="/devices/imaging" element={<ImagingDevicesList />} />
              <Route path="/devices/imaging/create" element={<ImagingDeviceCreate />} />
              <Route path="/devices/imaging/edit/:id" element={<ImagingDeviceEdit />} />
              <Route path="/devices/imaging/show/:id" element={<ImagingDeviceShow />} />
              <Route path="/devices/monitoring" element={<DeviceMonitoringDashboard />} />

              {/* Billing */}
              <Route path="/billing/entities" element={<BillingEntitiesPage />} />
              <Route path="/billing/entities/create" element={<BillingEntityCreate />} />
              <Route path="/billing/entities/edit/:id" element={<BillingEntityEdit />} />
              <Route path="/billing/entities/show/:id" element={<BillingEntityShow />} />
              <Route path="/billing/invoices" element={<InvoicesPage />} />
              <Route path="/billing/invoices/create" element={<InvoiceCreate />} />
              <Route path="/billing/invoices/edit/:id" element={<InvoiceEdit />} />
              <Route path="/billing/invoices/show/:id" element={<InvoiceShow />} />
              <Route path="/billing/payments" element={<PaymentsPage />} />
              <Route path="/billing/payments/create" element={<PaymentCreate />} />
              <Route path="/billing/payments/edit/:id" element={<PaymentEdit />} />
              <Route path="/billing/payments/show/:id" element={<PaymentShow />} />

              {/* Tax Management */}
              <Route path="/billing/tax-types" element={<TaxTypesPage />} />
              <Route path="/billing/tax-types/create" element={<TaxTypeCreate />} />
              <Route path="/billing/tax-types/edit/:id" element={<TaxTypeEdit />} />
              <Route path="/billing/tax-types/show/:id" element={<TaxTypeShow />} />
              <Route path="/billing/tax-exemptions" element={<TaxExemptionsPage />} />
              <Route path="/billing/tax-exemptions/create" element={<TaxExemptionCreate />} />
              <Route path="/billing/tax-exemptions/edit/:id" element={<TaxExemptionEdit />} />
              <Route path="/billing/tax-configurations" element={<TaxConfigurationsPage />} />
              <Route path="/billing/tax-configurations/create" element={<TaxConfigurationCreate />} />
              <Route path="/billing/tax-calculations" element={<TaxCalculationsPage />} />
              <Route path="/billing/tax-calculations/create" element={<TaxCalculationCreate />} />

              {/* Users — gated by SaaSBOXX feature flag. Default off →
                  redirect to admin.onprosoft.com */}
              <Route path="/users" element={<CentralUMGuard portalPath="/users"><TenantUsersPage /></CentralUMGuard>} />
              <Route path="/users/create" element={<CentralUMGuard portalPath="/users"><TenantUsersPage /></CentralUMGuard>} />
              <Route path="/users/edit/:id" element={<CentralUMGuard portalPath="/users"><TenantUsersPage /></CentralUMGuard>} />
              <Route path="/users/show/:id" element={<CentralUMGuard portalPath="/users"><TenantUsersPage /></CentralUMGuard>} />
              <Route path="/users/roles" element={<CentralUMGuard portalPath="/roles"><AccessRoles /></CentralUMGuard>} />
              {/* Legacy local-RBAC pages kept as fallback during cutover */}
              <Route path="/users/roles-legacy" element={<TenantRolesPage />} />
              <Route path="/users/roles-legacy/create" element={<TenantRolesPage />} />
              <Route path="/users/roles-legacy/edit/:id" element={<TenantRolesPage />} />
              <Route path="/users/roles-legacy/show/:id" element={<TenantRolesPage />} />
              <Route path="/users/sessions" element={<TenantUserSessionsPage />} />
              <Route path="/users/sessions/show/:id" element={<TenantUserSessionsPage />} />

              {/* Core Management */}
              <Route path="/core/companies" element={<CompanyProfile />} />
              <Route path="/core/companies/create" element={<CompanyCreate />} />
              <Route path="/core/companies/edit/:id" element={<CompanyEdit />} />
              <Route path="/core/companies/show/:id" element={<CompanyShow />} />
              <Route path="/core/branches" element={<BranchManagement />} />
              <Route path="/core/branches/create" element={<BranchCreate />} />
              <Route path="/core/branches/edit/:id" element={<BranchEdit />} />
              <Route path="/core/branches/show/:id" element={<BranchShow />} />
              <Route path="/core/addresses" element={<AddressManagement />} />
              <Route path="/core/addresses/create" element={<AddressCreate />} />
              <Route path="/core/addresses/edit/:id" element={<AddressEdit />} />
              <Route path="/core/addresses/show/:id" element={<AddressShow />} />
              <Route path="/core/settings" element={<TenantOrganizationSettings />} />
              <Route path="/core/currencies" element={<CurrencyManagementPage />} />
              <Route path="/core/branding" element={<BrandManagement />} />
              <Route path="/core/appearance" element={<AppearanceSettings />} />
              <Route path="/core/document-sequences" element={<DocumentNumbering />} />
              <Route path="/core/holidays" element={<HolidayCalendar />} />
              <Route path="/core/container-types" element={<ContainerTypes />} />
              <Route path="/core/audit-trail" element={<SettingsAuditTrail />} />
              <Route path="/core/danger-zone" element={<DangerZone />} />
              <Route path="/core/notifications" element={<CoreNotificationSettings />} />
              <Route path="/core/notification-templates" element={<UnifiedNotificationTemplateManager />} />

              {/* Support */}
              <Route path="/support/tickets" element={<SupportTickets />} />
              <Route path="/support/kb" element={<KnowledgeBase />} />

              {/* Legacy route redirects */}
              <Route path="/results" element={<ResultsWorklistPage />} />
              <Route path="/samples" element={<SamplesList />} />
              <Route path="/inventory" element={<InventoryList />} />
              <Route path="/devices" element={<AnalyzerDevicesList />} />

              {/* 404 */}
              <Route
                path="*"
                element={
                  <div className="flex items-center justify-center h-64">
                    <p className="text-muted-foreground">Page not found</p>
                  </div>
                }
              />
            </Route>
          </Routes>
          </Suspense>
          {/* Core Settings ▸ Brand Management: fetch the authoritative brand
              once inside the tree (main.tsx has already painted the cached
              one) and repaint if they differ. */}
          <BrandTheme />
          <UnsavedChangesNotifier />
          <DocumentTitleHandler
            handler={() => "LabBOXX - LIMS"}
          />
        </Refine>
        </NoAppWithoutTenant>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
